import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/react";
import { toast } from "@ndd/ui";
import { ApiRequestError, authenticatedApi } from "@/lib/api";

interface EditableSessionTitleProps {
  sessionId: string;
  initialClientName: string | null;
  fallback: string;
  className?: string;
}

export function EditableSessionTitle({
  sessionId,
  initialClientName,
  fallback,
  className,
}: EditableSessionTitleProps) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [savedName, setSavedName] = useState<string | null>(null);
  const committedName = savedName ?? initialClientName ?? "";
  const [draft, setDraft] = useState(committedName || fallback);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const displayValue = committedName || fallback;

  function startEditing() {
    setDraft(committedName || "");
    setIsEditing(true);
  }

  async function save() {
    const trimmed = draft.trim();
    if (!trimmed) {
      setIsEditing(false);
      return;
    }
    if (trimmed === committedName) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await authenticatedApi(getToken, `/api/sessions/${sessionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientName: trimmed }),
      });
      setSavedName(trimmed);
      toast.success("Name updated.");
      // Nearest equivalent of Next's router.refresh(): re-fetch the queries
      // that rendered this surface so the new name shows everywhere.
      void queryClient.invalidateQueries();
    } catch (error) {
      if (error instanceof ApiRequestError) {
        const message = (error.body as { error?: string } | undefined)?.error;
        toast.error(message ?? "Failed to rename.");
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsEditing(false);
    }
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        className={`bg-transparent border-b-2 border-interactive outline-none disabled:opacity-50 ${className ?? ""}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEditing}
      className={`text-left hover:bg-sand-100 rounded px-1 -mx-1 transition-colors ${className ?? ""}`}
    >
      {displayValue}
    </button>
  );
}
